import UserInterfaceCheck from '@mds/icons/Check';
import UserInterfaceInfo from '@mds/icons/Info';
import Link from 'components/ui/AppLink';
import { Box, Button, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { MessageView } from 'components/ui/Feedback';
import { useHistory, useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { useLogger } from '../../providers/logger/Logger';
import { LogAction, LogLevel } from '../../providers/logger/logger-types';
import { sanitizeRouteId } from '../../../utils/route-params.utils';
import { isValidMongoId } from '../../../utils/id-validation.utils';

interface InterestedInPremiumProps {
    articleTitle: string;
    deskEmail: string;
    lockedByCategory: boolean;
}

export const NOTIFY_REGISTER_INTEREST_IN_ARTICLE = gql`
    mutation notifyRegisteredInterestInArticle(
        $item: NotifyRegisteredInterestInArticleInput!
    ) {
        notifyRegisteredInterestInArticle(item: $item)
    }
`;

const InterestedInPremium: React.FC<InterestedInPremiumProps> = ({
    articleTitle,
    deskEmail,
    lockedByCategory,
}) => {
    const [success, setSuccess] = useState(false);

    const { _id: routeId } = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(routeId);
    const isValidArticleId = isValidMongoId(_id);
    const history = useHistory();

    const { printLog } = useLogger();

    const [notifyRegisteredInterest] = useMutation(
        NOTIFY_REGISTER_INTEREST_IN_ARTICLE
    );

    const registerInterestHandler = useCallback(async () => {
        if (!_id) {
            return;
        }

        try {
            await notifyRegisteredInterest({
                variables: {
                    item: {
                        articleId: _id,
                    },
                },
            });
            printLog(
                LogLevel.INFO,
                LogAction.RegisteredInPremium,
                'User registered interest in premium',
                {
                    articleId: _id,
                    interest: articleTitle,
                }
            );
            setSuccess(true);
        } catch (error) {
            printLog(
                LogLevel.ERROR,
                LogAction.RegisteredInterest,
                `Cannot register interest: ${error.message}`
            );
        }
    }, [notifyRegisteredInterest, _id, printLog, articleTitle]);

    const redirectHandler = () => {
        history.goBack();
    };

    const getRegisterInterestTextAndTitle = (
        lockedByCategory: boolean,
        deskEmail: string
    ) => {
        if (!lockedByCategory) {
            return {
                title: <>Interested in Insights Premium?</>,
                text: (
                    <>
                        Neon Insights Premium offers unparalleled market
                        research and analysis. Register below and a member of
                        the desk will be in touch to set up your personalised
                        and custom premium access.
                    </>
                ),
            };
        }

        return {
            title: <>This article is premium</>,
            text: (
                <>
                    If you are interested in becoming Marex research client,
                    please reach out to our sales team
                    {deskEmail && (
                        <>
                            {' '}
                            at{' '}
                            <Link
                                sx={{ fontSize: 'inherit' }}
                                ignoreRouter
                                to={`mailto:${deskEmail}`}
                            >
                                {deskEmail}
                            </Link>
                        </>
                    )}
                    .
                </>
            ),
        };
    };

    if (!isValidArticleId) {
        return (
            <MessageView
                styleName="primary"
                size="large"
                title="Article not found"
                primaryAction={{
                    label: 'Return to Neon Insights',
                    onClick: () => history.push('/'),
                }}
            />
        );
    }

    return (
        <Box
            id="register-in-premium"
            sx={{
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                px: { xs: 2, sm: 11, md: 14 },
                py: { xs: 4, sm: 8 },
                mb: { xs: 4, md: 0 },
                mx: 'auto',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    mx: 'auto',
                    border: 4,
                    borderColor: success ? 'success.main' : 'divider',
                    borderRadius: '50%',
                    width: 62.4,
                    height: 62.4,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {success ? (
                    <UserInterfaceCheck color="success" fontSize="large" />
                ) : (
                    <UserInterfaceInfo fontSize="large" color="disabled" />
                )}
            </Box>
            <Typography sx={{ mt: 6 }} variant="h3">
                {success ? (
                    <>We’ll be in touch</>
                ) : (
                    getRegisterInterestTextAndTitle(lockedByCategory, deskEmail)
                        .title
                )}
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: 'text.secondary',
                    mt: 2,
                }}
            >
                {success ? (
                    <>
                        Thank you for registering your interest in Neon Insights
                        Premium. We’ll be in touch shortly to set up your
                        access.
                    </>
                ) : (
                    getRegisterInterestTextAndTitle(lockedByCategory, deskEmail)
                        .text
                )}
            </Typography>
            {success ? (
                <Button
                    id="premium-or-locked-article-redirect-button"
                    sx={{ mt: 4 }}
                    variant="contained"
                    color="success"
                    size="medium"
                    onClick={redirectHandler}
                >
                    Continue
                </Button>
            ) : (
                !lockedByCategory && (
                    <Button
                        id="premium-or-locked-article-redirect-button"
                        sx={{ mt: 4 }}
                        variant="contained"
                        size="medium"
                        onClick={registerInterestHandler}
                    >
                        Register your interest
                    </Button>
                )
            )}
        </Box>
    );
};

export default InterestedInPremium;
